import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "@/data/content";

export default function Gallery() {
  const [active, setActive] = useState(null);

  const close = useCallback(() => setActive(null), []);
  const next = useCallback(
    () => setActive((i) => (i === null ? null : (i + 1) % GALLERY.length)),
    []
  );
  const prev = useCallback(
    () => setActive((i) => (i === null ? null : (i - 1 + GALLERY.length) % GALLERY.length)),
    []
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, close, next, prev]);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative bg-white py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <div className="eyebrow">Recent Work</div>
            <h2 className="font-serif-display text-[#111] mt-4 text-4xl md:text-6xl leading-[1.05] tracking-tight">
              A portfolio of <span className="italic text-[#C8A44D]">quiet</span> excellence.
            </h2>
          </div>
          <p className="text-[#555] max-w-md font-light leading-relaxed">
            Real homes, real finishes. Click any image to view full size.
          </p>
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {GALLERY.map((g, i) => (
            <button
              key={i}
              data-testid={`gallery-item-${i}`}
              onClick={() => setActive(i)}
              className="group relative mb-5 w-full break-inside-avoid overflow-hidden rounded-2xl block"
            >
              <img
                src={g.src}
                alt={g.label}
                loading="lazy"
                className={`w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05] ${
                  i % 3 === 0 ? "h-[420px]" : i % 3 === 1 ? "h-[300px]" : "h-[360px]"
                }`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute inset-x-0 bottom-0 p-5 text-left translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                <span className="text-[10px] tracking-[0.32em] uppercase text-[#C8A44D]">View</span>
                <div className="text-white font-serif-display text-xl mt-1">{g.label}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {active !== null && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-6"
          onClick={close}
        >
          <button
            data-testid="lightbox-close"
            onClick={close}
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2"
            aria-label="Close"
          >
            <X size={26} strokeWidth={1.5} />
          </button>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-4 md:left-10 text-white/80 hover:text-white p-3"
            aria-label="Previous"
          >
            <ChevronLeft size={32} strokeWidth={1.5} />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-4 md:right-10 text-white/80 hover:text-white p-3"
            aria-label="Next"
          >
            <ChevronRight size={32} strokeWidth={1.5} />
          </button>
          <img
            src={GALLERY[active].src}
            alt={GALLERY[active].label}
            className="max-h-[85vh] max-w-[92vw] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-8 inset-x-0 text-center text-white/80 text-sm tracking-[0.24em] uppercase">
            {GALLERY[active].label}
          </div>
        </div>
      )}
    </section>
  );
}
